import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import { ToolShell } from "@/components/tools/ToolShell";
import { getEsToolBySlug } from "@/lib/es-tools-catalog";
import { getEsToolContent } from "@/lib/es-tool-content";
import { ToolBody } from "../free-tools/$slug";

export const Route = createFileRoute("/es/$slug")({
  loader: ({ params }) => {
    if (params.slug === "pdf-tools") throw redirect({ to: "/es/pdf-tools", statusCode: 301 });
    if (params.slug === "sobre-nosotros" || params.slug === "acerca-de" || params.slug === "about") throw redirect({ to: "/es/about", statusCode: 301 });
    if (params.slug === "contacto" || params.slug === "contact") throw redirect({ to: "/es/contact", statusCode: 301 });
    if (params.slug === "politica-de-privacidad" || params.slug === "privacy-policy") throw redirect({ to: "/es/privacy-policy", statusCode: 301 });
    if (params.slug === "terminos-de-uso" || params.slug === "terms-of-use") throw redirect({ to: "/es/terms-of-use", statusCode: 301 });
    if (params.slug === "aviso-legal" || params.slug === "disclaimer") throw redirect({ to: "/es/disclaimer", statusCode: 301 });

    const tool = getEsToolBySlug(params.slug);
    if (!tool) throw notFound();
    return { tool };
  },
  head: ({ params }) => {
    const tool = getEsToolBySlug(params.slug);
    if (!tool) return { meta: [{ title: "Herramienta — Picamini.online" }] };

    const content = getEsToolContent(tool.slug, tool.name);
    const canonicalUrl = `https://picamini.online/es/${tool.slug}`;
    const enUrl = `https://picamini.online/${tool.enSlug}`;

    const webAppSchema = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `${tool.name} - Picamini.online`,
      "url": canonicalUrl,
      "description": content.seoDescription,
      "applicationCategory": "EducationalApplication",
      "operatingSystem": "All",
      "browserRequirements": "Requiere JavaScript. Requiere HTML5.",
      "inLanguage": "es",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
      },
      "featureList": content.features || [],
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Inicio",
          "item": "https://picamini.online/es/",
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": tool.name,
          "item": canonicalUrl,
        },
      ],
    };

    const faqSchema = content.faq && content.faq.length > 0 ? {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a,
        },
      })),
    } : null;

    const scripts: Array<{ type: string; children: string }> = [
      {
        type: "application/ld+json",
        children: JSON.stringify(webAppSchema),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(breadcrumbSchema),
      },
    ];

    if (faqSchema) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify(faqSchema),
      });
    }

    return {
      meta: [
        { title: content.seoTitle },
        { name: "description", content: content.seoDescription },
        {
          name: "keywords",
          content: tool.keywords?.join(", ") || `${tool.name.toLowerCase()}, herramientas gratis, picamini`,
        },
        { property: "og:site_name", content: "Picamini.online" },
        { property: "og:title", content: content.seoTitle },
        { property: "og:description", content: content.seoDescription },
        { property: "og:type", content: "website" },
        { property: "og:url", content: canonicalUrl },
        { property: "og:locale", content: "es_ES" },
        { property: "og:image", content: "https://picamini.online/og-image.png" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: `${tool.name} — Herramienta Gratuita en Picamini.online` },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: content.seoTitle },
        { name: "twitter:description", content: content.seoDescription },
        { name: "twitter:image", content: "https://picamini.online/og-image.png" },
        { name: "theme-color", content: "#4F46E5" },
        { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hrefLang: "es", href: canonicalUrl },
        { rel: "alternate", hrefLang: "en", href: enUrl },
        { rel: "alternate", hrefLang: "x-default", href: enUrl },
      ],
      scripts,
    };
  },
  component: EsToolPage,
});

function EsToolPage() {
  const { tool } = Route.useLoaderData();
  const content = getEsToolContent(tool.slug, tool.name);

  return (
    <ToolShell tool={tool} content={content} lang="es">
      <ToolBody key={tool.enSlug} slug={tool.enSlug} />
    </ToolShell>
  );
}
